/**
 * Terms of Service — public page (/terms)
 *
 * Australian-governed terms for JAI. Placeholders (highlighted via <Ph>)
 * must be replaced with real business details before launch, and the whole
 * document should be reviewed by a lawyer before you rely on it.
 */

import { LegalLayout, TOC, H2, P, UL, LI, Strong, Callout } from '../components/LegalLayout'

const SECTIONS = [
  { href: '#about', label: 'About JAI' },
  { href: '#eligibility', label: 'Eligibility' },
  { href: '#accounts', label: 'Your account' },
  { href: '#third-parties', label: 'Connected platforms' },
  { href: '#ai', label: 'AI advice & no guarantees' },
  { href: '#billing', label: 'Subscriptions & billing' },
  { href: '#acceptable-use', label: 'Acceptable use' },
  { href: '#ip', label: 'Intellectual property' },
  { href: '#beta', label: 'Beta & availability' },
  { href: '#liability', label: 'Disclaimers & liability' },
  { href: '#termination', label: 'Suspension & termination' },
  { href: '#changes', label: 'Changes to these terms' },
  { href: '#law', label: 'Governing law' },
  { href: '#contact', label: 'Contact us' },
]

export default function Terms() {
  return (
    <LegalLayout
      title="Terms of Service"
      updated="21 July 2026"
      otherDoc={{ to: '/privacy', label: 'Privacy Policy' }}
    >
      <P>
        These Terms of Service (<Strong>"Terms"</Strong>) are a binding agreement between you and{' '}
        JAI Fantasy Sports (ABN 62 279 215 204), a sole trader based in Victoria, Australia, which operates
        the JAI application and website (<Strong>"JAI"</Strong>, <Strong>"we"</Strong>, <Strong>"us"</Strong>, or{' '}
        <Strong>"our"</Strong>). By creating an account or using JAI, you agree to these Terms. If you do not
        agree, please do not use the service.
      </P>

      <TOC items={SECTIONS} />

      <H2 id="about">1. About JAI</H2>
      <P>
        JAI is an AI-powered fantasy sports co-manager. It provides research, rankings, projections, and
        recommendations to help you make fantasy sports decisions (initially for NFL and NBA, with more
        sports to follow). JAI provides information and suggestions only — you are always the one who makes
        your own roster, draft, trade, and waiver decisions.
      </P>

      <H2 id="eligibility">2. Eligibility</H2>
      <P>
        You must be at least 18 years old (or the age of majority where you live, if higher) to use JAI. By
        using JAI you confirm that you meet this requirement and that you can form a binding contract with us.
      </P>

      <H2 id="accounts">3. Your account</H2>
      <UL>
        <LI>You must provide accurate information when you sign up and keep it up to date.</LI>
        <LI>You are responsible for keeping your password secure and for all activity under your account.</LI>
        <LI>Tell us promptly at jai@play-jai.com if you suspect any unauthorised use of your account.</LI>
        <LI>One person, one account, unless we agree otherwise in writing.</LI>
      </UL>

      <H2 id="third-parties">4. Connected platforms &amp; no affiliation</H2>
      <Callout tone="info">
        <P>
          <Strong>JAI is an independent product.</Strong> JAI is not affiliated with, endorsed by, sponsored
          by, or associated with the NFL, NBA, ESPN, Yahoo, Sleeper, or any sports league, team, player, or
          fantasy platform. All team names, league names, player names, and logos are the property of their
          respective owners and are used for identification only.
        </P>
      </Callout>
      <P>
        JAI lets you connect third-party fantasy platforms (for example Sleeper, Yahoo, or ESPN). When you
        connect a platform, you authorise us to access your league, roster, and scoring data through that
        platform's interface so we can personalise JAI for you. Your use of those platforms remains governed
        by their own terms and privacy policies. We are not responsible for third-party services, their
        content, their availability, or any changes they make that affect JAI.
      </P>

      <H2 id="ai">5. AI-generated advice &amp; no guarantees</H2>
      <Callout tone="warn">
        <P>
          <Strong>JAI's recommendations are suggestions, not guarantees.</Strong> Rankings, projections, and
          advice are generated using artificial intelligence and automated data, and are provided for general
          information and entertainment only. They may be inaccurate, incomplete, or out of date, and
          real-world sports outcomes are inherently unpredictable. JAI does not guarantee any fantasy result,
          win, or financial gain. You are solely responsible for the decisions you make.
        </P>
      </Callout>
      <P>
        JAI is <Strong>not</Strong> betting or gambling advice and does not facilitate wagering. If your
        fantasy competition involves money or prizes, that arrangement is between you and your competition
        provider; you are responsible for gambling responsibly and complying with the laws that apply to you.
        Nothing in JAI is professional, financial, legal, or investment advice.
      </P>

      <H2 id="billing">6. Subscriptions &amp; billing</H2>
      <UL>
        <LI>
          Some features require a paid subscription. The price and billing period are shown at checkout.
          Payments are processed by our payment provider, Stripe; by subscribing you also agree to Stripe's
          terms.
        </LI>
        <LI>
          <Strong>Automatic renewal.</Strong> Paid subscriptions renew automatically at the end of each
          billing period — monthly plans every month, annual plans every year — and your payment method is
          charged the plan price shown at checkout each time, until you cancel. There is no minimum term,
          lock-in period, or cancellation fee.
        </LI>
        <LI>
          <Strong>How to cancel.</Strong> You can cancel at any time, yourself, from{' '}
          <Strong>Settings → Cancel plan</Strong> inside JAI — cancelling takes a couple of clicks and you
          never need to phone or email us to stop your subscription. You can also cancel through the Stripe
          billing portal via Settings → Subscription → Manage billing.
        </LI>
        <LI>
          <Strong>What cancelling does.</Strong> Cancellation stops all future charges. It takes effect at
          the end of the period you have already paid for: you keep full access to your paid plan until that
          date, and afterwards your account moves to the free Starter plan. Your account, connected leagues,
          and notes are not deleted. We will email you a confirmation showing the date your paid access ends.
        </LI>
        <LI>
          <Strong>Reminders we send you.</Strong> We email you before your free trial ends, so it never
          converts to a paid subscription without warning. If you are on an <Strong>annual</Strong> plan, we
          also email you between 15 and 45 days before each yearly renewal, telling you the renewal date, the
          amount, and how to cancel. If we change the price of your plan, we will tell you before the change
          affects you.
        </LI>
        <LI>
          <Strong>Refunds.</Strong> Because you keep full access for the remainder of the period you have
          paid for, fees already paid are not refunded on cancellation, to the extent permitted by law. This
          does not affect any refund or remedy required by the Australian Consumer Law or another applicable
          consumer law.
        </LI>
        <LI>
          We may change our prices or plans. If we do, we will give you reasonable notice before the change
          affects your subscription.
        </LI>
      </UL>
      <P>
        <Strong>Free trial (Pro and Elite plans).</Strong> New subscribers to Pro or Elite receive a{' '}
        <Strong>7-day free trial</Strong>. We collect your payment details when you start the trial, but you
        will not be charged during the trial period. If you do not cancel before the trial ends, your
        subscription automatically converts to a paid subscription at the plan's regular price shown at
        checkout, and your payment method is charged at the start of each billing period from then on. You
        can cancel at any time before the trial ends, at no cost, from{' '}
        <Strong>Settings → Cancel plan</Strong> — this stops the trial from converting to a paid subscription
        and no payment is taken. Free trials are limited to one per person or account, and we may withdraw or
        vary trial eligibility at our discretion.
      </P>
      <P>
        Nothing in these Terms excludes, restricts, or modifies any consumer guarantee, right, or remedy
        under the Australian Consumer Law that cannot lawfully be excluded.
      </P>

      <H2 id="acceptable-use">7. Acceptable use</H2>
      <P>When using JAI, you agree that you will not:</P>
      <UL>
        <LI>break any law, or infringe anyone's intellectual property or other rights;</LI>
        <LI>copy, scrape, resell, or commercially redistribute JAI's output or data without our written permission;</LI>
        <LI>reverse engineer, probe, or attempt to gain unauthorised access to JAI or its systems;</LI>
        <LI>introduce malware, or interfere with or place an unreasonable load on the service;</LI>
        <LI>use JAI in a way that breaches the terms of any connected third-party platform; or</LI>
        <LI>use JAI to harass, abuse, or harm other people.</LI>
      </UL>

      <H2 id="ip">8. Intellectual property</H2>
      <P>
        JAI, including its software, branding, design, and content, is owned by us or our licensors. We grant
        you a limited, personal, non-transferable, non-exclusive, revocable licence to use JAI for your own
        personal, non-commercial use, subject to these Terms.
      </P>
      <P>
        Content you create in JAI (such as notes and saved research) remains yours. You grant us a licence to
        host, store, and process that content only as needed to provide the service to you.
      </P>

      <H2 id="beta">9. Beta &amp; availability</H2>
      <P>
        JAI is currently in active development and may be offered as a beta. Features may change, be added, or
        be removed, and the service may sometimes be unavailable. JAI is provided on an "as is" and "as
        available" basis, and we do not guarantee uninterrupted or error-free access.
      </P>

      <H2 id="liability">10. Disclaimers &amp; limitation of liability</H2>
      <P>
        To the maximum extent permitted by law, and other than guarantees that cannot be excluded under the
        Australian Consumer Law, JAI is provided without warranties of any kind, and we exclude all implied
        warranties.
      </P>
      <P>
        Where our liability cannot be excluded but can be limited, our liability is limited (at our option) to
        re-supplying the service or paying the cost of re-supply. To the extent permitted by law, our total
        liability to you for any claim connected with JAI is capped at the greater of the amount you paid us
        in the 12 months before the claim, or USD $50. We are not liable for any indirect or
        consequential loss, or for any lost fantasy winnings, prizes, or opportunities.
      </P>

      <H2 id="termination">11. Suspension &amp; termination</H2>
      <P>
        You can stop using JAI and delete your account at any time. We may suspend or terminate your access if
        you breach these Terms, if we reasonably need to protect JAI or other users, or if we stop offering
        the service. Sections that by their nature should survive termination (such as intellectual property,
        disclaimers, and limitation of liability) will continue to apply.
      </P>

      <H2 id="changes">12. Changes to these Terms</H2>
      <P>
        We may update these Terms from time to time. If we make a material change, we will update the "Last
        updated" date above and take reasonable steps to let you know. By continuing to use JAI after a change
        takes effect, you accept the updated Terms.
      </P>

      <H2 id="law">13. Governing law</H2>
      <P>
        These Terms are governed by the laws of Victoria, Australia, and you and we submit to the
        non-exclusive jurisdiction of the courts of Victoria. This does not remove any consumer protection you
        are entitled to under the laws of the place where you live.
      </P>

      <H2 id="contact">14. Contact us</H2>
      <P>
        Questions about these Terms? Contact JAI Fantasy Sports at jai@play-jai.com, ABN{' '}
        62 279 215 204, Victoria, Australia.
      </P>
    </LegalLayout>
  )
}
